import { Injectable } from '@angular/core';
import { assert } from '../../../../../../../../core/assert/assert';
import { CalendarService } from '../../../../../../services/calendar.service';
import { TimeCoordinateMappingService } from '../../../../../../time-mapping/time-coordinate-mapping.service';
import { DateTimeHelper } from '../../../../../../util/date-time-helper';
import { DSTGapType, DateTimeHelperTZ } from '../../../../../../util/date-time-helper-tz';
import { ITimeBlockComponentItem } from '../../../../types/time-block-component-item-types';
import {
  CollisionHandlerService,
  IntersectionType,
} from '../../collision-handling/collision-handler.service';
import { TimeBlockDragResizeControllerService } from '../time-block-drag-resize-handling/time-block-drag-resize-controller.service';

@Injectable()
export class ResizeTimeBlockScheduleCalculatorService {
  constructor(
    private readonly timeBlockDragResizeControllerService: TimeBlockDragResizeControllerService,
    private readonly timeCoordinateMappingService: TimeCoordinateMappingService,
    private readonly calendarService: CalendarService,
  ) {}

  public calculateSchedule(): void {
    this.calculateTimeBlockStart();
    this.calculateTimeBlockEnd();

    const resizingTimeBlock = this.timeBlockDragResizeControllerService.TransformationTimeBlock;

    if (this.hitActiveTimeBlockBottomEdge(resizingTimeBlock)) {
      let start = resizingTimeBlock.timeBlockModel.start;
      const end = resizingTimeBlock.timeBlockModel.end;
      const minimumDuration =
        this.calendarService.model.calendarProperties.minimumTimeBlockDuration;

      start = DateTimeHelper.subMinutes(end, minimumDuration);
      resizingTimeBlock.timeBlockModel.start = start;
      resizingTimeBlock.timeBlockModel.end = end;
    }

    assert(
      !DateTimeHelper.isBefore(
        resizingTimeBlock.timeBlockModel.end,
        resizingTimeBlock.timeBlockModel.start,
      ),
      'End date is before start date.',
    );
  }

  public hitActiveTimeBlockBottomEdge(timeBlock: ITimeBlockComponentItem) {
    return (
      timeBlock.timeBlockModel.isActive &&
      (CollisionHandlerService.collisionType === IntersectionType.IntersectCalendarBottomEdge ||
        CollisionHandlerService.collisionType === IntersectionType.IntersectWithDragBottomEdge)
    );
  }

  public calculateTimeBlockStart(): void {
    // Only calculate start if the resized time block part is the head part.
    const resizingTimeBlock = this.timeBlockDragResizeControllerService.TransformationTimeBlock;
    if (resizingTimeBlock.timeBlockModel.partNumber !== 0) {
      return;
    }

    const ppm = this.timeCoordinateMappingService.pixelsPerMinuteCalculated$.value;
    assert(ppm !== null);

    const startOfDay = DateTimeHelper.startOfDay(resizingTimeBlock.timeBlockModel.start);
    const minutesStart = resizingTimeBlock.timeBlockModel.timeBlockViewType.geometryData.top / ppm;
    // eslint-disable-next-line prefer-const
    let { date, gapType, winterToSummer } = DateTimeHelperTZ.minutesToDate(
      minutesStart,
      startOfDay,
    );
    if (gapType === DSTGapType.WithinGap || (gapType === DSTGapType.AfterGap && winterToSummer)) {
      date = DateTimeHelper.subHours(date, 1);
    } else if (gapType === DSTGapType.AfterGap && !winterToSummer) {
      date = DateTimeHelper.addHours(date, 1);
    }
    resizingTimeBlock.timeBlockModel.start = date;
  }

  public calculateTimeBlockEnd(): void {
    // Only calculate end if the resized time block part is the tail part.
    const resizingTimeBlock = this.timeBlockDragResizeControllerService.TransformationTimeBlock;
    if (
      resizingTimeBlock.timeBlockModel.partNumber !==
      resizingTimeBlock.timeBlockModel.partCount - 1
    ) {
      return;
    }

    const ppm = this.timeCoordinateMappingService.pixelsPerMinuteCalculated$.value;
    assert(ppm !== null);

    const geometryData = resizingTimeBlock.timeBlockModel.timeBlockViewType.geometryData;

    // Mouse cursor is below the calendar bottom edge.
    if (
      geometryData.calendarTopEdgeToTimeBlockBottomEdge >=
      this.calendarService.model.geometryData.calendarBodyHeight
    ) {
      const timeBlockPartDate = DateTimeHelper.addDays(
        resizingTimeBlock.timeBlockModel.start,
        resizingTimeBlock.timeBlockModel.partNumber,
      );
      resizingTimeBlock.timeBlockModel.end = DateTimeHelper.startOfNextDay(timeBlockPartDate);
    } else {
      // Set the time otherwise.
      const startOfDay = DateTimeHelper.startOfDay(resizingTimeBlock.timeBlockModel.end);
      const minutesEnd = geometryData.calendarTopEdgeToTimeBlockBottomEdge / ppm;
      // eslint-disable-next-line prefer-const
      let { date, gapType, winterToSummer } = DateTimeHelperTZ.minutesToDate(
        minutesEnd,
        startOfDay,
      );
      if (gapType === DSTGapType.AfterGap && winterToSummer) {
        date = DateTimeHelper.subHours(date, 1);
      } else if (gapType === DSTGapType.AfterGap && !winterToSummer) {
        date = DateTimeHelper.addHours(date, 1);
      }

      resizingTimeBlock.timeBlockModel.end = date;
    }
  }
}
